import { useSearchParams, useNavigate } from "react-router-dom";
import { useClientPosition } from "../hooks/useQueue";
import NotificationManager from "../components/NotificationManager";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader, AlertCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import * as api from "../services/api";
import { ConfirmationModal } from "../components/ConfirmationModal";
import { useBranding } from "../contexts/BrandingContext";
import { useTranslation } from "react-i18next";
import { useBusinessLabels } from "../hooks/useBusinessLabels";
import type { BusinessType } from "../types";
import LoadingScreen from "../components/ui/LoadingScreen";
import PoweredBy from "../components/ui/PoweredBy";
import YulqoFace from "../components/ui/YulqoFace";
import { FACE_COLORS } from "../config/platformBrand";
import {
  clearTurnDismissed,
  readTurnDismissed,
  skipQueueRestoreOnce,
  writeTurnDismissed,
} from "../utils/queueSession";
import styles from "./QueueStatusPage.module.css";

const QueueStatusPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const clientId = searchParams.get("clientId");
  const { branding, applyOrganizationTheme } = useBranding();
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [turnDismissed, setTurnDismissed] = useState(
    () => (clientId ? readTurnDismissed(clientId) : false),
  );

  useEffect(() => {
    if (!clientId) {
      navigate("/", { replace: true });
    }
  }, [clientId, navigate]);

  const { data, isLoading, error } = useClientPosition(clientId || "");

  const orgPlan = (data as { organization?: { plan?: string } } | undefined)?.organization?.plan ?? "FREE";
  const showPoweredBy = orgPlan !== "PRO";

  const businessType: BusinessType =
    (data?.organization?.businessType as BusinessType) ||
    (data?.artist?.businessType as BusinessType) ||
    "OTHER";
  const labels = useBusinessLabels(businessType);

  useEffect(() => {
    const settings =
      data?.organization?.brandingSettings || data?.artist?.brandingSettings;
    if (!settings) return;
    applyOrganizationTheme({
      primaryColor: settings.primaryColor,
      secondaryColor: settings.secondaryColor,
    });
  }, [data, applyOrganizationTheme]);

  useEffect(() => {
    if (!clientId) return;
    setTurnDismissed(readTurnDismissed(clientId));
  }, [clientId]);

  useEffect(() => {
    if (!clientId || !data?.status) return;
    if (data.status !== "IN_PROGRESS") {
      clearTurnDismissed(clientId);
      setTurnDismissed(false);
    }
  }, [data?.status, clientId]);

  useEffect(() => {
    if (data?.status === "COMPLETED" || data?.status === "CANCELLED") {
      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith("clientId_") &&
          localStorage.getItem(key) === clientId
        ) {
          localStorage.removeItem(key);
        }
      });
    }
  }, [data?.status, clientId]);

  const dismissTurnOverlay = (): void => {
    if (clientId) writeTurnDismissed(clientId);
    setTurnDismissed(true);
  };

  const goHome = (): void => {
    skipQueueRestoreOnce();
    navigate("/", { replace: true });
  };

  const leaveMutation = useMutation({
    mutationFn: (id: string) => api.removeClient(id),
    onSuccess: () => {
      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith("clientId_") &&
          localStorage.getItem(key) === clientId
        ) {
          localStorage.removeItem(key);
        }
      });
      queryClient.invalidateQueries({ queryKey: ["clientPosition"] });
      navigate("/", { replace: true });
    },
  });

  const confirmLeave = () => {
    leaveMutation.mutate(clientId!);
    setLeaveModalOpen(false);
  };

  const confetti = useMemo(
    () =>
      Array.from({ length: 28 }, (_, i) => ({
        left: `${Math.random() * 100}%`,
        color: FACE_COLORS[i % FACE_COLORS.length],
        duration: `${3 + Math.random() * 3}s`,
        delay: `${Math.random() * 2}s`,
        round: i % 3 === 0,
      })),
    [],
  );

  if (!clientId || isLoading) {
    return <LoadingScreen message={t("common.loading")} />;
  }

  if (error || !data) {
    const is404 = (error as { response?: { status?: number } })?.response?.status === 404;

    return (
      <div className={styles.page}>
        <div className={styles.errorCard}>
          <AlertCircle
            className={`${styles.errorCardIcon} ${is404 ? styles.iconWarning : styles.iconError}`}
            aria-hidden="true"
          />
          <h2 className={styles.errorCardTitle}>
            {is404 ? t("status.clientNotFound") : t("status.connectionError")}
          </h2>
          <p className={styles.errorCardText}>
            {is404 ? t("status.removedOrExpired") : t("status.connectionErrorDesc")}
          </p>
          <button type="button" onClick={goHome} className={styles.primaryBtn}>
            {t("common.back")}
          </button>
        </div>
      </div>
    );
  }

  const isYourTurn = data.position === 1 && data.status === "WAITING";
  const isTattooInProgress = data.status === "IN_PROGRESS";
  const position = data.position ?? 0;
  const ahead = data.status === "WAITING" ? Math.max(0, position - 1) : 0;
  const eta = data.estimatedWaitTime || 0;
  const around = new Date(Date.now() + eta * 60000).toTimeString().slice(0, 5);
  const eventName =
    data.event?.name ||
    branding.homeTitle ||
    data.artist?.brandingSettings?.homeTitle ||
    t("status.title");

  const ticketLabel =
    data.status === "CANCELLED"
      ? t("status.cancelled")
      : data.status === "COMPLETED"
        ? t("status.completed")
        : isTattooInProgress
          ? t("look.called")
          : isYourTurn
            ? t("look.getReady")
            : ahead <= 2
              ? t("look.getCloser")
              : t("look.canWander");

  const showTurnOverlay = isTattooInProgress && !turnDismissed;

  return (
    <div className={styles.page}>
      <div className={styles.wrap}>
        <div className={styles.tkWrap}>
          <article className={styles.ticket} aria-live="polite">
            <div className={styles.tkTop}>
              <div className={styles.tkPlace}>
                <div>
                  <strong>{eventName}</strong>
                  {data.name ? <small>{data.name}</small> : null}
                </div>
                {data.status === "WAITING" ? (
                  <span className={styles.live}>{t("look.live")}</span>
                ) : null}
              </div>
              <div className={styles.tkNumwrap}>
                <div className={styles.tkNum}>{position || "—"}</div>
                <span className={styles.hand}>{t("look.thisIsYourNumber")}</span>
              </div>
              <p className={styles.tkLabel}>{ticketLabel}</p>
            </div>
            <div className={styles.tkBody}>
              {data.status === "WAITING" ? (
                <>
                  <div className={styles.walk}>
                    <YulqoFace color="#8FA58A" size={30} />
                    {Array.from({ length: Math.min(ahead, 8) }, (_, i) => (
                      <YulqoFace key={i} index={i + 2} size={30} mood="flat" />
                    ))}
                    <YulqoFace color="#D9774B" size={40} me />
                  </div>
                  <div className={styles.walkMeta}>
                    <span>
                      {t("look.serving")} <b>{Math.max(1, position - ahead)}</b>
                    </span>
                    <span>
                      <b>{ahead}</b> {t("look.ahead")}
                    </span>
                  </div>
                  <div className={styles.tkGrid}>
                    <div>
                      <span>{t("look.eta")}</span>
                      <b>{ahead === 0 ? "—" : `~${eta} ${t("common.minutes")}`}</b>
                    </div>
                    <div>
                      <span>{t("look.around")}</span>
                      <b>{ahead === 0 ? "—" : around}</b>
                    </div>
                  </div>
                </>
              ) : null}

              {data.status === "WAITING" ? (
                <NotificationManager
                  clientId={clientId!}
                  pushEnabled={data.pushEnabled}
                  emailEnabled={data.emailEnabled}
                  whatsappEnabled={data.whatsappEnabled}
                  emailAvailable={data.event?.emailNotifications}
                  whatsappAvailable={!!data.phone && data.event?.whatsappNotifications}
                  pushAvailable={data.event?.pushNotifications}
                  disabled={false}
                />
              ) : null}

              {data.status === "WAITING" ? (
                <div className={styles.tkActions}>
                  <button
                    type="button"
                    className={styles.tkLeave}
                    onClick={() => setLeaveModalOpen(true)}
                    disabled={leaveMutation.isPending}
                  >
                    {leaveMutation.isPending ? (
                      <Loader className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      t("status.leaveQueue")
                    )}
                  </button>
                </div>
              ) : (
                <button type="button" onClick={goHome} className={styles.primaryBtn}>
                  {t("common.back")}
                </button>
              )}
            </div>
          </article>

          <aside className={styles.aside}>
            <h3>
              {t("look.relaxTitle")} <em>{t("look.relaxEm")}</em>.
            </h3>
            <ul>
              <li>
                <span className={styles.ic} style={{ background: "var(--butter-soft)" }}>☕</span>
                <div>
                  <b>{t("look.coffeeTitle")}</b>
                  <span>{t("look.coffeeDesc")}</span>
                </div>
              </li>
              <li>
                <span className={styles.ic} style={{ background: "var(--sage-soft)" }}>🔔</span>
                <div>
                  <b>{t("look.bellTitle")}</b>
                  <span>{t("look.bellDesc")}</span>
                </div>
              </li>
              <li>
                <span className={styles.ic} style={{ background: "var(--terra-soft)" }}>🚶</span>
                <div>
                  <b>{t("look.walkTitle")}</b>
                  <span>{t("look.walkDesc")}</span>
                </div>
              </li>
            </ul>
          </aside>
        </div>
        <div className={styles.footer}>
          <PoweredBy style={showPoweredBy ? undefined : { display: "none" }} />
        </div>
      </div>

      {showTurnOverlay ? (
        <div className={styles.turn} role="alertdialog" aria-label={t("look.yourTurn")}>
          <div className={styles.confetti} aria-hidden="true">
            {confetti.map((bit, i) => (
              <i
                key={i}
                style={{
                  left: bit.left,
                  background: bit.color,
                  animationDuration: bit.duration,
                  animationDelay: bit.delay,
                  borderRadius: bit.round ? "50%" : "3px",
                }}
              />
            ))}
          </div>
          <YulqoFace color="#D9774B" size={110} />
          <h2>
            {t("look.yourTurnLead")} <em>{t("look.yourTurnEm")}</em>!
          </h2>
          <div className={styles.tNum}>{t("look.numberLabel", { n: position || "" })}</div>
          <p>{labels.presentNow || t("look.presentHint")}</p>
          <button type="button" className={styles.turnOk} onClick={dismissTurnOverlay}>
            {t("look.goThere")}
          </button>
        </div>
      ) : null}

      <ConfirmationModal
        isOpen={leaveModalOpen}
        onClose={() => setLeaveModalOpen(false)}
        onConfirm={confirmLeave}
        title={t("common.confirmLeave")}
        message={t("common.confirmLeaveMessage")}
        variant="danger"
        icon="danger"
      />
    </div>
  );
};

export default QueueStatusPage;
